import { createPrepaymentAPI } from '@/service/user/order'
import Big from 'big.js'
import dayjs from 'dayjs'
import { now, uniqueId } from 'lodash-es'

export const isLaunchFinished = ref(false) // 全局启动完成标志

// 格式化图片路径
export const formatImageUrl = (
  target,
  options: { isVideo?: boolean; hasPoster?: boolean; minify?: boolean } = {
    isVideo: false,
    hasPoster: false,
    minify: true,
  },
) => {
  const baseUrl = import.meta.env.VITE_OSS_SERVER_URL
  let suffix = import.meta.env.VITE_OSS_IMG_QUANTITY_SUFFIX
  if (options.minify) {
    suffix = ''
  }
  const videoImgSuffix = '?x-oss-process=video/snapshot,t_100,f_png,w_0,h_0,m_fast'

  if (!target) return '' // target为空，直接返回空字符串

  // 提取公共逻辑：如果是http开头的URL，直接返回原路径
  const formatUrl = (item) => (item.startsWith('http') ? item : baseUrl + item)

  // 检测是否是 SVG 文件
  const isSvg = (item) => item.endsWith('.svg')

  // 检测是否已经包含指定的后缀
  const hasAddedSuffix = (item) => {
    const suffixRegex = new RegExp(`${suffix.replace(/\?/g, '\\?')}$`)
    const videoImgSuffixRegex = new RegExp(`${videoImgSuffix.replace(/\?/g, '\\?')}$`)
    return suffixRegex.test(item) || videoImgSuffixRegex.test(item)
  }

  // 针对数组和单个 target 做判断
  if (Array.isArray(target)) {
    return target.map((item) => {
      const formattedUrl = formatUrl(item) // 确保前缀添加逻辑
      if (isSvg(item) || hasAddedSuffix(item)) {
        return formattedUrl // SVG 或已包含后缀，不添加后缀
      }
      if (options.isVideo) {
        return options.hasPoster ? formattedUrl + videoImgSuffix : formattedUrl
      }
      return formattedUrl + suffix // 普通图片
    })
  } else {
    const formattedUrl = formatUrl(target) // 确保前缀添加逻辑
    if (isSvg(target) || hasAddedSuffix(target)) {
      return formattedUrl // SVG 或已包含后缀，不添加后缀
    }
    if (options.isVideo) {
      return options.hasPoster ? formattedUrl + videoImgSuffix : formattedUrl
    }
    return formattedUrl + suffix // 普通图片
  }
}

// 还原图片路径
export const unformatImageUrl = (target) => {
  const baseUrl = import.meta.env.VITE_OSS_SERVER_URL
  const suffix = import.meta.env.VITE_OSS_IMG_QUANTITY_SUFFIX
  const videoImgSuffix = '?x-oss-process=video/snapshot,t_100,f_png,w_0,h_0,m_fast'

  if (!target) return '' // 如果target为空，返回空字符串

  // 去除前缀和后缀
  const removePrefixAndSuffix = (item) => {
    let result = item

    // 如果路径包含前缀，去除前缀
    if (result.startsWith(baseUrl)) {
      result = result.replace(baseUrl, '')
    }

    // 如果路径包含普通图片后缀或视频缩略图后缀，去除后缀
    if (result.endsWith(suffix)) {
      result = result.replace(suffix, '')
    } else if (result.endsWith(videoImgSuffix)) {
      result = result.replace(videoImgSuffix, '')
    }

    return result
  }

  // 针对数组和单个 target 做判断
  if (Array.isArray(target)) {
    return target.map(removePrefixAndSuffix)
  } else {
    return removePrefixAndSuffix(target)
  }
}

export const getOssVideoPosterUrl = (url) => {
  return formatImageUrl(url, { isVideo: true, hasPoster: true })
}

// 套餐编辑数据格式化函数
export const formatMealData = (data) => {
  return {
    available_date_begin: data.info?.available_date_begin || null,
    available_date_end: data.info?.available_date_end || null,
    booking: data.info?.booking,
    community_merchant_id: data.community_merchant_id,
    community_merchant_store_id: data.community_merchant_store_id,
    community_merchant_store_ids: data.community_merchant_store_ids,
    description: data.description || '',
    discount_shared: data.info?.discount_shared,
    env_picture: data.info?.env_picture || [],
    free_package: data.info?.free_package,
    id: data.id,
    main_picture: data.info?.main_picture || [],
    meal_service: data.info?.meal_service || null,
    menu_picture: data.info?.menu_picture || [],
    num_of_people: data.info?.num_of_people || null,
    original_price: data.original_price || null,
    period_of_validity: data.info?.period_of_validity,
    private_room: data.info?.private_room,
    purchase_limit: data.info?.purchase_limit || null,
    sales_date_begin: data.info?.sales_date_begin || null,
    sales_date_end: data.info?.sales_date_end || null,
    sales_price: data.sales_price,
    set_meal_category: data.set_meal_category,
    set_meal_category_id: data.set_meal_category_id,
    category_id_arr: data.category_id_arr,
    set_meal_information: data.set_meal_information,
    suitable_for_stores: data.suitable_for_stores,
    status: data.status,
    stock: data.inventory.stock,
    sub_title: data.sub_title || null,
    thumb: data.thumb,
    title: data.title,
    validity_days: data.info?.validity_days || null,
    video: data.info?.video,
    valid_date: [
      new Date(data.info?.available_date_begin).getTime(),
      new Date(data.info?.available_date_end).getTime(),
    ],
  }
}
// 将时间戳转成 "2024-04-01 21:49:19" 格式
export const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hour = date.getHours().toString().padStart(2, '0')
  const minute = date.getMinutes().toString().padStart(2, '0')
  const second = date.getSeconds().toString().padStart(2, '0')

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}
// 将时间戳转成 "2024-04-01" 格式
export const formatDate = (timestamp) => {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * 把接口返回的日期时间字符串（后端口径 `YYYY-MM-DD HH:mm:ss`）格式化为 `YYYY-MM-DD`。
 *
 * 只接受 `YYYY-MM-DD` 开头的值，且解析结果必须与原值逐段相等：`9999-99-99 99:99:99`
 * 这类占位值会被 dayjs / Date 静默归一化成 `10007-06-11`（`isValid()` 仍为 true），
 * 必须靠逐段比对拦下。无法识别或越界的值返回空串，调用方据此不渲染，避免展示假数据（R14-1）。
 * 与 `formatDate`（入参为时间戳）区分：本函数入参是后端返回的日期字符串。
 */
export const formatApiDate = (value?: string | null) => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})(?!\d)/.exec(value ?? '')
  if (!matched) {
    return ''
  }
  const [, year, month, day] = matched
  const date = dayjs(`${year}-${month}-${day}`)
  const isSameDate =
    date.isValid() &&
    date.year() === Number(year) &&
    date.month() === Number(month) - 1 &&
    date.date() === Number(day)

  return isSameDate ? `${year}-${month}-${day}` : ''
}

// 停车订单详情使用
// 格式化停车时间 把分钟转成 "00天00时00分" 格式
export const formatParkTime = (minutes) => {
  const days = Math.floor(minutes / (60 * 24))
  const hours = Math.floor((minutes % (60 * 24)) / 60)
  const mins = minutes % 60
  // 使用 padStart 方法补零
  const formattedDays = String(days).padStart(2, '0')
  const formattedHours = String(hours).padStart(2, '0')
  const formattedMinutes = String(mins).padStart(2, '0')

  return `${formattedDays}天${formattedHours}时${formattedMinutes}分`
}
// 停车订单列表使用
// 把 parkTimeText: "39天15时28分"和parkTimeText: "3分" 统一转成 xx天xx时xx分，
export const formatParkTimeText = (parkTimeText) => {
  // 将字符串按天、时、分分割
  const daysMatch = parkTimeText.match(/(\d+)\s*天/)
  const hoursMatch = parkTimeText.match(/(\d+)\s*时/)
  const minutesMatch = parkTimeText.match(/(\d+)\s*分/)

  // 如果存在天、时或分，将其转换为数字，不存在则为0
  const days = daysMatch ? parseInt(daysMatch[1]) : 0
  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0

  return `${days}天${hours}时${minutes}分`
}
/**
 * 通用的下单支付流程函数
 * @param {Object} createOrderParams - 创建订单的参数
 * @param {Function} createOrderAPI - 创建订单的API函数
 * @param {Function} toast - 'wot-design-uni'的toast
 * @param {Object} options - 额外的选项，如订单类型等
 * @param {Function} callback - 创建订单成功的回调函数 //todo 参数定义问题
 *
 */
export const processOrderAndPay = async (
  createOrderParams,
  createOrderAPI,
  toast,
  options: Record<string, any> = {},
  callback,
) => {
  try {
    // 创建订单
    const orderRes = await createOrderAPI(createOrderParams)
    if (orderRes.code !== 200) {
      console.error(orderRes.message)
      toast.error(orderRes.message)
      return
    }
    console.log('创建订单data:', orderRes.data)

    // 订单支付
    const prepayParams = {
      order_id: orderRes.data.id,
      payment_type: options.payment_type || 4, // 1：微信支付 4：泊链支付（默认） 5：积分兑换
      order_type: options.order_type || 1,
    }
    const payInfo = await createPrepaymentAPI(prepayParams)
    console.log('预支付', payInfo)
    if (payInfo.code !== 200) {
      toast.error(payInfo.message)
      return
    }
    // 非积分兑换跳转支付链接
    if (options.payment_type !== 5) {
      location.href = payInfo.data.prepayment_info.payurl
    }
    // 注意 这个callback可能不需要参数
    callback(orderRes.data.id)
  } catch (error) {
    console.error(error)
    toast.error(error.data.message || '下单失败，请稍后重试')
  }
}
// 调用例子
// 提交订单
// const createOrder = async () => {
//   const params = { good_id: id.value, good_type: 1, count: number.value, description: remark.value }
//   await processOrderAndPay(params, createOrderAPI)
// }

// 格式化资源组数据结构
export function formatResourceData(data) {
  const dataList = []
  // url前缀 todo判断data可否遍历
  data.artwork.forEach((item) => {
    const obj: Record<string, any> = {}
    obj.id = item.id
    obj.file_id = item.file_id
    obj.title = item.title
    obj.type = item.file.mime_type
    obj.url = item.file_full_url // icon图片的完整路径
    obj.endpoint = item.endpoint // 跳转目标的路径或链接
    obj.apply_id = item.apply_id // 跳转目标的appid
    obj.endpoint_type = item.endpoint_type // 跳转类型
    obj.code = item.code
    // item.attach  自定义标识
    dataList.push({ ...obj, ...item.attach })
  })
  return dataList
}

/**
 * 用于处理不同类型的资源跳转。
 *
 * @param {number} type 跳转类型：1=普通链接, 2=Uni-app内部跳转, 3=外部网页, 4=小程序重启
 * @param {string} target 目标URL，对应不同的跳转类型
 * @param {string} appId 小程序的appId，仅当 type=4 时使用
 * @param {object} [options] 可选参数配置对象
 * @param {function} [options.callback] 跳转后的回调函数，仅在 type=2 时使用
 * @param {object} [options.params] 其他附加参数，可在回调中使用
 */
export const resourceGroupJump = (
  type,
  target,
  appId,
  options: { callback?: ((target?: any) => void) | null; params?: any } = {},
) => {
  if (type !== 1 && !target) {
    console.error('跳转目标 URL 不能为空')
    return
  }

  if (type === 1) {
    // 类型1，静态资源
  } else if (type === 2) {
    // 检查是否有回调函数
    if (options?.callback) {
      // 执行回调函数，并传递附加参数
      options.callback(options.params || target)
    } else {
      // 默认的页面跳转逻辑
      uni.navigateTo({
        url: target,
      })
    }
  } else if (type === 3) {
    // 跳转到外部链接
    location.href = target
  } else if (type === 4) {
    console.log('跳转小程序功能待完善')
  } else {
    console.error('未知的跳转类型')
  }
}

// 根据优惠券值和类型，返回优惠券名称
export const formatCouponValue = (value, type) => {
  const couponType = {
    1: '抵扣券',
    2: '折扣券',
    3: '免费券',
    4: '减时券',
  }
  switch (type) {
    case 1:
      return `${value}元${couponType[type]}` // 抵扣券
    case 2:
      return `${value / 10}折${couponType[type]}` // 折扣券
    case 3:
      return `${couponType[type]}` // 免费券
    case 4:
      return `${value}小时${couponType[type]}` // 减时券
    default:
      return '未知优惠券' // 处理未知类型
  }
}

/**
 * 计算商品的总价，支持折扣，并截断保留两位小数。
 *
 * @param {number} quantity - 商品数量，必须为正整数。
 * @param {number} price - 商品单价，必须为非负数。
 * @param {number} [discount=100] - 折扣值，范围为 1 到 100，例如 95 表示 9.5 折。
 * @param {number} [expressFee=100] - 运费，默认为 0，
 * @returns {string} - 返回截断后的总价，保留两位小数。
 * @throws {Error} - 如果输入无效或折扣超出范围，抛出错误。
 */
export function calculateTotal(quantity, price, discount = 100, expressFee = 0) {
  if (isNaN(quantity) || isNaN(price) || isNaN(discount)) {
    throw new Error('无效输入：数量、价格和折扣必须是数字。')
  }

  if (discount < 1 || discount > 100) {
    throw new Error('折扣必须在 1 到 100 之间。')
  }

  const bigQuantity = new Big(quantity)
  const bigPrice = new Big(price)
  const bigExpressFee = new Big(expressFee)
  const discountMultiplier = new Big(discount).div(100)
  const discountedPrice = bigPrice.times(discountMultiplier)
  const total = bigQuantity.times(discountedPrice).plus(bigExpressFee)

  // 截断保留两位小数，并返回格式化后的字符串 （保留两位小数）这里toFixed不参与计算，只是用来格式化小数位数
  return total.round(2, Big.roundDown).toFixed(2)
}

/**
 * 计算单个商品的总价、折扣后的总价和优惠金额，并保留两位小数。目前用于单个商品的确认订单页面的价格明细，
 * 为什加这个，因为要拿到优惠明细
 *
 * @param {number} quantity - 商品数量，必须为正整数。
 * @param {number} price - 商品单价，必须为非负数。
 * @param {number} [discount=100] - 折扣值，范围为 1 到 100，例如 95 表示 9.5 折。
 * @returns {Object} - 包含总价、折扣后的总价和优惠金额的对象，所有金额截断保留两位小数。
 * @throws {Error} - 如果输入无效或折扣超出范围，抛出错误。
 */
export function calculateItemDetails(quantity, price, discount = 100, expressFee = 0) {
  if (isNaN(quantity) || isNaN(price) || isNaN(discount)) {
    throw new Error('无效输入：数量、价格和折扣必须是数字。')
  }

  if (discount < 1 || discount > 100) {
    throw new Error('折扣必须在 1 到 100 之间。')
  }

  const bigQuantity = new Big(quantity)
  const bigPrice = new Big(price)
  const discountMultiplier = new Big(discount).div(100)
  const bigExpressFee = new Big(expressFee)

  // 计算总价和折扣后的价格
  const originalTotal = bigQuantity.times(bigPrice).round(2, Big.roundDown)
  const discountedTotal = originalTotal.times(discountMultiplier).round(2, Big.roundDown)

  // 加上运费
  const totalAmount = discountedTotal.plus(bigExpressFee).round(2, Big.roundDown)

  // 计算优惠金额：原价 - 折扣后价
  const discountAmount = originalTotal.minus(discountedTotal)

  return {
    originalTotal: originalTotal.toFixed(2),
    discountedTotal: totalAmount.toFixed(2),
    discountAmount: discountAmount.toFixed(2),
  }
}

/**
 * 计算总价与实付价格的差值 用于vvip计算优惠显示
 * @param {number|string} totalPrice - 总价
 * @param {number|string} paidPrice - 实付价格
 * @returns {string} 返回差值的字符串表示
 */
export function calculateDiffMinus(totalPrice, paidPrice) {
  const total = new Big(totalPrice)
  const paid = new Big(paidPrice)
  const price = total.minus(paid).toFixed(2)
  return price
}

// 电话号码掩码显示
export const maskPhoneNumber = (phone) => {
  // 检查输入是否为有效的电话号码格式
  if (typeof phone === 'string' && phone.length > 4) {
    return phone.replace(/(\d{3})\d+(\d{4})/, '$1****$2')
  }
  return phone
}

// 电话号码和座机校验
export const validatePhone = (phone) => {
  // 手机号正则：以1开头，第二位为3-9，总共11位数字
  const mobilePattern = /^1[3-9]\d{9}$/
  // 座机号正则：区号以0开头，2到3位区号，7到8位号码，可以带或不带区号后面的连字符
  const landlinePattern = /^0\d{2,3}-?\d{7,8}$/

  if (phone && (mobilePattern.test(phone) || landlinePattern.test(phone))) {
    return Promise.resolve()
  } else {
    return Promise.reject('手机号码或座机号码无效')
  }
}

// utils/color.js

/**
 * 调整颜色亮度的函数
 * @param {string} color - 传入的颜色值，可以是 hex 格式，例如 #FF5733
 * @param {number} amount - 调整的亮度值，正数为增亮，负数为减暗
 * @returns {string} 返回调整后的颜色 hex 值
 */
export function adjustColor(color, amount) {
  // 匹配颜色字符串中的 hex 颜色值（如果是 linear-gradient，只取其中的纯色）
  let hexColor = color.match(/#[A-Fa-f0-9]{6}|#[A-Fa-f0-9]{3}/)?.[0] || '#cccccc'

  let usePound = false // 是否带有 '#' 符号

  // 如果颜色值以 '#' 开头，去掉它以便后续处理
  if (hexColor[0] === '#') {
    hexColor = hexColor.slice(1)
    usePound = true // 记录该颜色值原本有 '#'
  }

  // 将十六进制颜色值转换为整数
  const num = parseInt(hexColor, 16)

  // 提取红、绿、蓝三种颜色分量，并根据 `amount` 进行调整
  let r = (num >> 16) + amount // 提取红色分量
  let b = ((num >> 8) & 0x00ff) + amount // 提取蓝色分量
  let g = (num & 0x0000ff) + amount // 提取绿色分量

  // 限制颜色分量的范围在 0 到 255 之间
  r = r > 255 ? 255 : r < 0 ? 0 : r
  b = b > 255 ? 255 : b < 0 ? 0 : b
  g = g > 255 ? 255 : g < 0 ? 0 : g

  // 将调整后的红、绿、蓝分量重新组合为 hex 颜色值，并返回
  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0')
}

export function getRootSize() {
  const systemInfo = uni.getSystemInfoSync() // 获取设备信息
  const windowWidth = systemInfo.windowWidth // 获取设备窗口宽度
  const designWidth = 375 // 设计稿宽度，通常为 375px
  const baseFontSize = 16 // 设计稿中的基准字体大小
  // 根据设备的窗口宽度动态计算根字体大小
  const rootSize = (windowWidth / designWidth) * baseFontSize
  return rootSize // 返回根字体大小
}

// 将 px 转换为 rem
export function pxToRem(size) {
  const remSize = (size / 16) * getRootSize() // 计算 rem 值
  return remSize // 返回 rem 值
}

/**
 * 将设计稿像素值(px)转换为当前设备的真实物理像素值
 * @param {number} designPx - 设计稿上的像素值（如100）
 * @param {number} [designWidth=375] - 设计稿宽度，默认375
 * @returns {number} 设备上的物理像素值
 */
export function pxToDevicePx(designPx: number, designWidth = 375): number {
  const systemInfo = uni.getSystemInfoSync()
  const devicePixelRatio = systemInfo.pixelRatio // 设备像素比
  const windowWidth = systemInfo.windowWidth // 设备CSS像素宽度

  // 设计稿px先转为当前设备的CSS像素，再乘以DPR得到物理像素
  const cssPx = (designPx / designWidth) * windowWidth
  const devicePx = Math.round(cssPx * devicePixelRatio)
  return devicePx
}

/**
 * 将前端数据转换为符合后端要求的格式
 *
 * @param data - 需要转换的表单数据对象
 * @param rules - 字段默认值规则对象
 * @returns 转换后的数据对象
 */
export const formatToBackend = (data, rules) => {
  const transformedData = {}
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key]
      const defaultValue = rules[key]
      // 对空字符串字段应用默认值
      if (Array.isArray(value) && value.length === 0) {
        transformedData[key] = defaultValue
      } else if (value === '' && defaultValue !== undefined) {
        transformedData[key] = defaultValue
      } else {
        transformedData[key] = value // 其他字段原样保留
      }
    }
  }

  return transformedData
}
// 生成唯一值
export function getUniqueId() {
  // 获取当前时间戳
  const timestamp = now()

  // 生成唯一数字 ID
  const uniqueValue = uniqueId()

  // 组合时间戳和唯一数字 ID
  return `${timestamp}${uniqueValue}`
}
// 睡眠函数
export function sleep(delay = 0) {
  return new Promise((resolve) => {
    setTimeout(resolve, delay)
  })
}
// 图片转canvas
const ImageToCanvas = (img) => {
  // 创建一个 canvas 元素
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d') // 获取2D上下文

  // 设置 canvas 的宽度和高度为图像的宽高
  canvas.width = img.width
  canvas.height = img.height
  // 将图像绘制到 canvas 上
  ctx.drawImage(img, 0, 0, img.width, img.height)

  return canvas // 返回 canvas 对象
}
/**
 * 解决图片跨域问题，将网络图片URL转为base64 URL。
 * @exports url2Base64
 * @param {string} src 网络图片URL
 * @returns {Promise} Promise对象返回base64 URL
 *
 * @example
 * Dom2Image.url2Base64("http://test.com/image.png").then(url=>{});
 */
const url2Base64 = async (src) =>
  new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.src = src
    img.onload = () => {
      const canvas = ImageToCanvas(img)
      const url = canvas.toDataURL('image/png')
      resolve(url)
    }
    img.onerror = () => {
      resolve(new Error('转换base64失败'))
    }
  })

/**
 * 格式化金额为千分位显示，保留2位小数
 * @param {number|string} num - 要格式化的数字
 * @returns {string} 格式化后的字符串（如 "5,306.00"）
 */
export function formatMoney(num) {
  let number = Number(num)
  if (isNaN(number)) {
    return '0.00'
  }

  // 处理负数
  const isNegative = number < 0
  number = Math.abs(number)

  // 格式化并还原负数
  const result = number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return isNegative ? `-${result}` : result
}

/**
 * 通用 URL 参数解析方法
 * 兼容以下两种格式：
 * 1. https://example.com/?q=vue&page=1#/search
 * 2. https://example.com/#/search?q=vue&page=1
 */
export function parseUniversalUrlParams(
  url: string = window.location.href,
): Record<string, string> {
  const params: Record<string, string> = {}

  try {
    // 分离基础部分和 hash 部分
    const [basePart, hashPart = ''] = url.split('#')

    // 解析 ? 参数（基础部分）
    const baseSearchParams = new URLSearchParams(basePart.split('?')[1] || '')
    baseSearchParams.forEach((value, key) => {
      params[key] = decodeURIComponent(value)
    })

    // 解析 hash 中的 ? 参数（如果有）
    const hashPathParts = hashPart.split('?')
    if (hashPathParts.length > 1) {
      const hashSearchParams = new URLSearchParams(hashPathParts[1])
      hashSearchParams.forEach((value, key) => {
        params[key] = decodeURIComponent(value)
      })
    }
  } catch (e) {
    console.error('URL参数解析失败:', e)
  }

  return params
}

// 拨打电话
export const makeCall = (tel) => {
  uni.makePhoneCall({
    phoneNumber: tel,
    fail: () => {
      console.log('拨打电话失败')
    },
  })
}

// 请求竞态
export function createCancelTask(asyncTask) {
  const NOOP = () => {}
  let cancel = NOOP
  return (...args) => {
    return new Promise((resolve, reject) => {
      cancel()
      cancel = () => {
        resolve = reject = NOOP
      }
      asyncTask(...args)
        .then((res) => {
          resolve(res)
        })
        .catch((err) => {
          reject(err)
        })
      // asyncTask(...args).then(res => resolve(res), err => reject(err))
      // asyncTask(...args).then(resolve, reject) 不能用这个
    })
  }
}
/**
 * 把参数拼接到 hash 后面的 buildUrl
 * @param base 原始 url（可带已有 query / hash）
 * @param params 待追加的参数对象
 * @returns 拼接后的完整 url
 */
export function buildHashUrl(
  base: string,
  params: Record<
    string,
    string | number | boolean | Array<string | number | boolean> | null | undefined
  >,
): string {
  const [path, hash = ''] = base.split('#', 2)
  const [hashPath, oldQuery = ''] = hash.split('?', 2)

  const urlSearch = new URLSearchParams(oldQuery)

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return

    urlSearch.delete(key)

    if (Array.isArray(value)) {
      value.forEach((v) => urlSearch.append(key, String(v)))
    } else {
      urlSearch.set(key, String(value))
    }
  })

  const queryString = urlSearch.toString()
  const newHash = hashPath + (queryString ? `?${queryString}` : '')
  return path + (newHash ? `#${newHash}` : '')
}

// // 请求竞态：后发起的请求自动忽略前面未完成的
// export function createCancelTask(asyncTask) {
//   let version = 0                    // 全局版本号

//   return (...args) => {
//     const myVersion = ++version      // 本次调用的版本号

//     return asyncTask(...args).then(
//       res => (myVersion === version ? res : Promise.reject(new Error('应该取消'))),
//       err => (myVersion === version ? Promise.reject(err) : Promise.reject(new Error('应该取消')))
//     )
//   }
// }

/**
 * 获取当前页面路径
 * @returns 当前页面路径
 */
export function getCurrentPath() {
  const pages = getCurrentPages()
  const currentPage = pages[pages.length - 1]
  return currentPage.route || ''
}
